import cn from 'classnames';
import React, { useState } from 'react';
import { ArrayOfReactElement } from '../common/Types';

interface AccordionProps {
  accordionMultiPanel?: boolean;
  accordionPanels: ArrayOfReactElement<AccordionPanelProps>;
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (props, ref) => {
    const { accordionMultiPanel = false, accordionPanels } = props;

    const [openPanels, setOpenPanels] = useState(
      accordionPanels.map(p => false)
    );
    const togglePanel = (index: number) => () => {
      setOpenPanels(prev =>
        prev.map((panel, i) => {
          return index === i ? !panel : accordionMultiPanel ? panel : false;
        })
      );
    };

    const renderAccordionPanels = () => {
      return React.Children.map(accordionPanels, (panel, panelIndex) => {
        if (panel) {
          return React.cloneElement(panel, {
            open: openPanels[panelIndex],
            togglePanel: togglePanel(panelIndex)
          });
        }
      });
    };

    return (
      <div ref={ref} className="clr-accordion">
        {renderAccordionPanels()}
      </div>
    );
  }
);

interface AccordionPanelProps {
  open?: boolean;
  accordionTitle: React.ReactElement;
  accordionDescription: React.ReactElement;
  accordionContent: React.ReactElement;
  togglePanel?: () => void;
}

export const AccordionPanel = React.forwardRef<
  HTMLDivElement,
  AccordionPanelProps
>((props, ref) => {
  const {
    open,
    accordionTitle,
    accordionDescription,
    accordionContent,
    togglePanel
  } = props;

  const panelStateClassName = cn('clr-accordion-panel-inactive', {
    'clr-accordion-panel-open': open
  });

  const renderAccordionContent = () =>
    React.cloneElement(accordionContent, {
      open
    });

  return (
    <div ref={ref} className="clr-accordion-panel">
      <div role="group" className={panelStateClassName}>
        <div className="clr-accordion-header">
          <button
            type="button"
            className="clr-accordion-header-button"
            onClick={togglePanel}>
            <span className="clr-accordion-status">
              <clr-icon shape="angle" dir="right" class="clr-accordion-angle" />
              <span className="clr-accordion-number"></span>
              <clr-icon
                shape="exclamation-circle"
                class="clr-accordion-error-icon"
              />
              <clr-icon
                shape="check-circle"
                class="clr-accordion-complete-icon"
              />
            </span>
            {accordionTitle}
            {accordionDescription}
          </button>
        </div>
        <div role="region">{renderAccordionContent()}</div>
      </div>
    </div>
  );
});

interface TitleProps {
  children: React.ReactNode;
}

export const AccordionTitle = React.forwardRef<HTMLDivElement, TitleProps>(
  (props, ref) => {
    const { children, ...attrs } = props;
    return (
      <div ref={ref} className="clr-accordion-title" {...attrs}>
        {children}
      </div>
    );
  }
);

interface DescriptionProps {
  children: React.ReactNode;
}

export const AccordionDescription = React.forwardRef<
  HTMLDivElement,
  DescriptionProps
>((props, ref) => {
  const { children, ...attrs } = props;
  return (
    <div ref={ref} className="clr-accordion-description" {...attrs}>
      {children}
    </div>
  );
});

interface ContentProps {
  children: React.ReactNode;
  open?: boolean;
}

export const AccordionContent = React.forwardRef<HTMLDivElement, ContentProps>(
  (props, ref) => {
    const { open, children } = props;
    return open ? (
      <div ref={ref} className="clr-accordion-content">
        <div className="clr-accordion-inner-content">{children}</div>
      </div>
    ) : null;
  }
);
